import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Grid2, Typography } from '@mui/material'
import React, { useContext } from 'react'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { GRID_CONFIG } from '../utils/patientFormConfig'
import { CheckboxFormField, FormField } from './FormField'
import PatientAlternativeEmails from './PatientAlternativeEmails'
import PatientAttendants from './PatientAttendants'
import PatientCommunicationToPref from './PatientCommunicationToPref'
import { usePatientFormI18n } from '../utils/patientFormI18n'

export const PatientMoreDetails = ({
  patientDetails,
  editMode = true,
  formHook,
  validationHook,
  visibilityHook,
  makeAnonymousPatient,
  dontUseMoreDetailsAccordian = false
}) => {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const { copy } = usePatientFormI18n()

  const MoreDetailsContent = () => (
    <Grid2 container spacing={2}>
          {/* Email in More Details */}
          {visibilityHook.shouldShowEmailInMoreDetails && (
            <FormField
              label={copy.emailLabel}
              value={patientDetails?.email}
              onChange={formHook.handleEmailChange}
              disabled={!editMode}
              gridConfig={GRID_CONFIG}
              helperText={copy.emailHelper}
              validationError={validationHook.getFieldValidation('email').errorMessage}
            />
          )}

          {visibilityHook.shouldShowAlternativeEmailsInMoreDetails && (
            <PatientAlternativeEmails
              alternativeEmails={patientDetails?.altEmails}
              onAdd={formHook.addAlternativeEmail}
              onUpdate={formHook.updateAlternativeEmail}
              onRemove={formHook.removeAlternativeEmail}
              editMode={editMode}
              validationHook={validationHook}
              label={copy.alternateEmailLabel}
              helperText={copy.alternateEmailHelper}
            />
          )}

          {/* Attendants */}
          {visibilityHook.isFieldVisible('attendants') && (
            <PatientAttendants
              attendants={patientDetails?.attendants}
              onAdd={formHook.addAttendant}
              onUpdate={formHook.updateAttendant}
              onRemove={formHook.removeAttendant}
              editMode={editMode}
              validationHook={validationHook}
            />
          )}

          {/* Communication to Attendant */}
          {visibilityHook.isFieldVisible('communicationToAttendant') &&
            Array.isArray(patientDetails?.attendants) &&
            patientDetails?.attendants?.length > 0 && (
              <CheckboxFormField
                label={copy.communicationToAttendantLabel}
                checked={!!patientDetails?.communicationToAttendant}
                onChange={formHook.handleCommunicationToAttendantChange}
                disabled={!editMode}
                checkboxProps={{ size: 'small' }}
                gridConfig={GRID_CONFIG}
                className="flex-column"
              />
            )}

          {/* Language */}
          {visibilityHook.isFieldVisible('language') && (
            <FormField
              label={copy.patientPreferredLanguageLabel}
              gridConfig={GRID_CONFIG}
              helperText={copy.patientPreferredLanguageHelper}
            >
              <CustomSelect
                isDisabled={!editMode}
                value={patientDetails?.defaultCommunicationLanguage}
                id="defaultCommunicationLanguage"
                options={unSecureSetupData?.appLanguages}
                getOptionValue={(optn) => optn?.id}
                menuPlacement="auto"
                getOptionLabel={(optn) =>
                  `${optn?.language} ${optn?.id !== 'en' ? `(${optn?.localLanguageName})` : ''}`
                }
                selectType={selectTypes.string}
                onChangeEvent={formHook.handleLanguageChange}
              />
            </FormField>
          )}

          {/* Pincode */}
          {visibilityHook.isFieldVisible('pincode') && (
            <FormField
              label={copy.pincodeLabel}
              type="number"
              value={patientDetails?.pincode}
              onChange={formHook.handlePincodeChange}
              disabled={!editMode}
              gridConfig={GRID_CONFIG}
            />
          )}

          {/* Note */}
          {visibilityHook.isFieldVisible('note') && (
            <FormField
              label={copy.noteLabel}
              value={patientDetails?.note}
              onChange={formHook.handleNoteChange}
              disabled={!editMode}
              gridConfig={GRID_CONFIG}
            />
          )}

          {/* Referred By */}
          {visibilityHook.isFieldVisible('referredBy') && (
            <FormField
              label={copy.referredByLabel}
              value={patientDetails?.referredBy}
              onChange={formHook.handleReferredByChange}
              disabled={!editMode}
              gridConfig={GRID_CONFIG}
            />
          )}

          {/* Communication Preferences */}
          {visibilityHook.isFieldVisible('communicationToPref') && (
            <PatientCommunicationToPref
              communicationToPref={patientDetails?.communicationToPref}
              onPreferenceChange={formHook.handleCommunicationToPrefChange}
              onAddReceiver={formHook.addCommunicationReceiver}
              onUpdateReceiver={formHook.updateCommunicationReceiver}
              onRemoveReceiver={formHook.removeCommunicationReceiver}
              editMode={editMode}
              validationHook={validationHook}
            />
          )}
    </Grid2>
  )

  if (dontUseMoreDetailsAccordian) {
    return <MoreDetailsContent />
  }

  return (
    <Accordion disableGutters elevation={0} defaultExpanded={!editMode}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Typography component="span">{copy.moreDetailsTitle}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <MoreDetailsContent />
      </AccordionDetails>
    </Accordion>
  )
}

export default PatientMoreDetails
